import { useState } from 'react';
import { Check, Zap, Rocket, Building2, Star, ArrowRight, Bitcoin, Wallet } from 'lucide-react';

// Supported cryptocurrencies
const CRYPTO_OPTIONS = [
    { symbol: 'USDT', name: 'Tether USD', icon: '💵', stable: true },
    { symbol: 'USDC', name: 'USD Coin', icon: '💲', stable: true },
    { symbol: 'ETH', name: 'Ethereum', icon: '⟠', stable: false },
    { symbol: 'BTC', name: 'Bitcoin', icon: '₿', stable: false },
    { symbol: 'SOL', name: 'Solana', icon: '◎', stable: false },
    { symbol: 'BNB', name: 'BNB', icon: '🔶', stable: false },
];

const PLANS = [
    {
        id: 'free',
        name: 'Free Trial',
        price: 0,
        period: '7 days',
        creditsPerDay: 50,
        description: 'Try AI-powered marketing free',
        features: [
            '50 AI credits/day',
            '1 project',
            '1 team member',
            'Basic analytics',
            'Community support',
        ],
        cta: 'Start Free Trial',
        popular: false,
        icon: Zap,
    },
    {
        id: 'launchpad',
        name: 'Launch Pad',
        price: 9,
        period: '/month',
        creditsPerDay: 2000,
        description: 'Perfect for solopreneurs & small teams',
        features: [
            '2,000 AI credits/day',
            '5 projects',
            '1 team member',
            'All AI modules',
            'Email support',
            'Priority processing',
            'Export reports',
        ],
        cta: 'Get Started',
        popular: true,
        icon: Rocket,
    },
    {
        id: 'scale',
        name: 'Scale',
        price: 49,
        period: '/month',
        creditsPerDay: 10000,
        description: 'For growing agencies & teams',
        features: [
            '10,000 AI credits/day',
            'Unlimited projects',
            '5 team members',
            'All AI modules',
            'Priority support',
            'BYOK option (use your own API keys)',
            'Advanced analytics',
            'White-label reports',
            'API access',
        ],
        cta: 'Start Scaling',
        popular: false,
        icon: Building2,
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        price: null,
        period: 'Custom',
        creditsPerDay: null,
        description: 'For large organizations',
        features: [
            'Unlimited AI credits (BYOK)',
            'Unlimited projects',
            'Unlimited team members',
            'Dedicated support',
            'Custom integrations',
            'SLA guarantee',
            'On-premise option',
            'Custom training',
        ],
        cta: 'Contact Sales',
        popular: false,
        icon: Star,
    },
];

const FAQ = [
    {
        q: 'What are AI credits?',
        a: 'AI credits are used each time you generate content, analyze data, or use any AI-powered feature. Different tasks use different amounts of credits based on complexity.',
    },
    {
        q: 'What is BYOK (Bring Your Own Keys)?',
        a: 'Scale and Enterprise plans let you connect your own API keys from OpenAI, Anthropic, or other providers. You pay those providers directly at their rates, and we don\'t charge for AI usage.',
    },
    {
        q: 'Can I upgrade or downgrade anytime?',
        a: 'Yes! You can change plans at any time. Upgrades are prorated, and downgrades take effect at the next billing cycle.',
    },
    {
        q: 'What happens if I run out of credits?',
        a: 'You\'ll receive a notification when you\'re running low. Free users can wait until tomorrow or upgrade. Paid users can purchase additional credits or use BYOK.',
    },
    {
        q: 'Is there a money-back guarantee?',
        a: 'Yes! All paid plans include a 14-day money-back guarantee. No questions asked.',
    },
    {
        q: 'What cryptocurrencies do you accept?',
        a: 'We accept USDT, USDC, ETH, BTC, SOL, and BNB through dadyPay. Stablecoins (USDT/USDC) are recommended for predictable pricing.',
    },
    {
        q: 'How do crypto payments work?',
        a: 'Select your plan, choose your preferred cryptocurrency and network, then send the exact amount to the provided wallet address. Your plan activates automatically once the transaction is confirmed.',
    },
];

export default function Pricing() {
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
    const yearlyDiscount = 0.20; // 20% off yearly

    const getPrice = (plan: typeof PLANS[0]) => {
        if (plan.price === null) return 'Custom';
        if (plan.price === 0) return 'Free';

        const price = billingPeriod === 'yearly'
            ? Math.round(plan.price * (1 - yearlyDiscount))
            : plan.price;

        return `$${price}`;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            {/* Header */}
            <header className="border-b border-gray-700/50 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <Zap className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-white">AI Operations</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <a href="/login" className="text-gray-300 hover:text-white transition">
                                Sign In
                            </a>
                            <a
                                href="/register"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
                            >
                                Get Started
                            </a>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Simple, Transparent Pricing
                    </h1>
                    <p className="text-xl text-gray-400 mb-8">
                        Start free, scale as you grow. No hidden fees, no surprises.
                    </p>

                    {/* Billing Toggle */}
                    <div className="inline-flex items-center bg-gray-800 rounded-full p-1 mb-12">
                        <button
                            onClick={() => setBillingPeriod('monthly')}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition ${billingPeriod === 'monthly'
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingPeriod('yearly')}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${billingPeriod === 'yearly'
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            Yearly
                            <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                                Save 20%
                            </span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="pb-20 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {PLANS.map((plan) => {
                            const Icon = plan.icon;
                            return (
                                <div
                                    key={plan.id}
                                    className={`relative rounded-2xl p-6 ${plan.popular
                                        ? 'bg-gradient-to-br from-blue-600 to-purple-600 ring-2 ring-blue-400'
                                        : 'bg-gray-800/50 border border-gray-700'
                                        }`}
                                >
                                    {plan.popular && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                            <span className="bg-yellow-500 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
                                                🚀 MOST POPULAR
                                            </span>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3 mb-4">
                                        <div
                                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${plan.popular ? 'bg-white/20' : 'bg-gray-700'
                                                }`}
                                        >
                                            <Icon className={`w-5 h-5 ${plan.popular ? 'text-white' : 'text-blue-400'}`} />
                                        </div>
                                        <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                                    </div>

                                    <p className={`text-sm mb-4 ${plan.popular ? 'text-blue-100' : 'text-gray-400'}`}>
                                        {plan.description}
                                    </p>

                                    <div className="mb-6">
                                        <span className="text-4xl font-bold text-white">{getPrice(plan)}</span>
                                        <span className={plan.popular ? 'text-blue-100' : 'text-gray-400'}>
                                            {plan.period}
                                        </span>
                                    </div>

                                    {plan.creditsPerDay && (
                                        <div
                                            className={`text-sm mb-6 ${plan.popular ? 'text-blue-100' : 'text-gray-400'}`}
                                        >
                                            <span className="font-semibold text-white">
                                                {plan.creditsPerDay.toLocaleString()}
                                            </span>{' '}
                                            AI credits/day
                                        </div>
                                    )}

                                    <ul className="space-y-3 mb-8">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <Check
                                                    className={`w-5 h-5 shrink-0 ${plan.popular ? 'text-green-300' : 'text-green-400'
                                                        }`}
                                                />
                                                <span className={plan.popular ? 'text-white' : 'text-gray-300'}>
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        className={`w-full py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 ${plan.popular
                                            ? 'bg-white text-blue-600 hover:bg-gray-100'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        {plan.cta}
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Credit Calculator */}
            <section className="py-20 px-4 bg-gray-800/30">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-white text-center mb-4">
                        What Can You Do With Credits?
                    </h2>
                    <p className="text-gray-400 text-center mb-12">
                        Here's roughly what you can accomplish with each plan
                    </p>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4">Free Trial (50/day)</h3>
                            <ul className="space-y-2 text-gray-400 text-sm">
                                <li>• 5 blog post outlines</li>
                                <li>• 10 social media posts</li>
                                <li>• 1 SEO analysis</li>
                                <li>• 3 email drafts</li>
                            </ul>
                        </div>
                        <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-xl p-6 border border-blue-500/30">
                            <h3 className="text-lg font-semibold text-white mb-4">Launch Pad (2,000/day)</h3>
                            <ul className="space-y-2 text-gray-300 text-sm">
                                <li>• 20 full blog posts</li>
                                <li>• 200 social media posts</li>
                                <li>• 40 SEO analyses</li>
                                <li>• 100 email campaigns</li>
                                <li>• Full marketing for 5 clients</li>
                            </ul>
                        </div>
                        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4">Scale (10,000/day)</h3>
                            <ul className="space-y-2 text-gray-400 text-sm">
                                <li>• 100 full blog posts</li>
                                <li>• 1,000 social media posts</li>
                                <li>• 200 SEO analyses</li>
                                <li>• 500 email campaigns</li>
                                <li>• Full marketing for 25+ clients</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 px-4">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-white text-center mb-12">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-4">
                        {FAQ.map((item, i) => (
                            <details
                                key={i}
                                className="bg-gray-800/50 rounded-xl border border-gray-700 group"
                            >
                                <summary className="px-6 py-4 cursor-pointer text-white font-medium flex items-center justify-between">
                                    {item.q}
                                    <span className="text-gray-400 group-open:rotate-180 transition-transform">
                                        ▼
                                    </span>
                                </summary>
                                <div className="px-6 pb-4 text-gray-400">{item.a}</div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* Accepted Crypto */}
            <section className="py-12 px-4 bg-gray-800/30">
                <div className="max-w-4xl mx-auto text-center">
                    <h3 className="text-xl font-semibold text-white mb-6">
                        <Wallet className="w-5 h-5 inline mr-2" />
                        Pay with Crypto via dadyPay
                    </h3>
                    <div className="flex flex-wrap justify-center gap-4">
                        {CRYPTO_OPTIONS.map((crypto) => (
                            <div
                                key={crypto.symbol}
                                className={`px-4 py-2 rounded-lg border ${crypto.stable
                                        ? 'border-green-500/50 bg-green-500/10'
                                        : 'border-gray-600 bg-gray-800/50'
                                    }`}
                            >
                                <span className="mr-2">{crypto.icon}</span>
                                <span className="text-white font-medium">{crypto.symbol}</span>
                                {crypto.stable && (
                                    <span className="ml-2 text-xs text-green-400">Recommended</span>
                                )}
                            </div>
                        ))}
                    </div>
                    <p className="text-gray-400 text-sm mt-4">
                        Stablecoins (USDT/USDC) recommended for predictable pricing
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-12">
                        <h2 className="text-3xl font-bold text-white mb-4">
                            Ready to Transform Your Marketing?
                        </h2>
                        <p className="text-blue-100 mb-8">
                            Join thousands of marketers using AI to work smarter, not harder.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="/register"
                                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition inline-flex items-center justify-center gap-2"
                            >
                                Start Free Trial
                                <ArrowRight className="w-4 h-4" />
                            </a>
                            <a
                                href="/register?plan=launchpad"
                                className="bg-blue-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-400 transition inline-flex items-center justify-center gap-2"
                            >
                                <Bitcoin className="w-4 h-4" />
                                Pay with Crypto - $9/mo
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-700/50 py-8 px-4">
                <div className="max-w-7xl mx-auto text-center text-gray-400 text-sm">
                    <p>© 2026 AI Operations. All rights reserved.</p>
                    <p className="mt-2">
                        Powered by Sundarban AI • Payments via dadyPay • 14-day money-back guarantee
                    </p>
                </div>
            </footer>
        </div>
    );
}
